import api from '../apis/constants';

export interface CreatePromotionRequest {
    code: string;
    name: string;
    description: string;
    type: string; // 'percentage', 'fixed_amount'
    value: number;
    time_constraints: {
        start_date: string;
        end_date: string;
    };
    status: string; // 'active', 'inactive'
    quantity: {
        total: number;
        used: number;
    };
    constraints: {
        minimum_order_amount: number;
        maximum_discount_amount: number | null;
    };
}

export interface PromotionResponse {
    id?: string;
    _id?: string;
    code: string;
    name: string;
    description: string;
    type: string;
    value: number;
    time_constraints: {
        start_date: string;
        end_date: string;
    };
    status: string;
    quantity: {
        total: number;
        used: number;
    };
    constraints: {
        minimum_order_amount: number;
        maximum_discount_amount: number | null;
    };
    created_at?: string;
    updated_at?: string;
    image_url?: string;
    is_valid?: boolean;
}

export interface PromotionsListResponse {
    promotions: PromotionResponse[];
    total: number;
    page: number;
    totalPages: number;
}

export interface PromotionFilterParams {
    status?: string;
    type?: string;
    page?: number;
    limit?: number;
}

const promotionApiService = {
    /**
     * Create a new promotion
     * @param data The promotion data to create
     * @returns The created promotion
     */
    async createPromotion(data: CreatePromotionRequest): Promise<PromotionResponse> {
        try {
            const response = await api.post('/promotions', data);
            return response.data;
        } catch (error) {
            console.error('Error creating promotion:', error);
            throw error;
        }
    },

    /**
     * Get a list of all promotions
     * @param filterParams Optional filter parameters like status
     * @returns List of promotions
     */
    async getPromotions(filterParams?: PromotionFilterParams): Promise<PromotionsListResponse> {
        try {
            const response = await api.get('/promotions', {
                params: filterParams
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching promotions:', error);
            throw error;
        }
    },

    /**
     * Get a promotion by ID
     * @param id The promotion ID
     * @returns The promotion with the specified ID
     */
    async getPromotionById(id: string): Promise<PromotionResponse> {
        try {
            const response = await api.get(`/promotions/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching promotion with ID ${id}:`, error);
            throw error;
        }
    },

    /**
     * Update an existing promotion
     * @param id The promotion ID
     * @param data The updated promotion data
     * @returns The updated promotion
     */
    async updatePromotion(id: string, data: Partial<CreatePromotionRequest>): Promise<PromotionResponse> {
        try {
            const response = await api.put(`/promotions/${id}`, data);
            return response.data;
        } catch (error) {
            console.error(`Error updating promotion with ID ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete a promotion
     * @param id The promotion ID
     */
    async deletePromotion(id: string): Promise<void> {
        try {
            await api.delete(`/promotions/${id}`);
        } catch (error) {
            console.error(`Error deleting promotion with ID ${id}:`, error);
            throw error;
        }
    },

    /**
     * Update status for multiple promotions
     * @param ids Array of promotion IDs to update
     * @param status New status ('active' or 'inactive')
     */
    async updatePromotionsStatus(ids: string[], status: 'active' | 'inactive'): Promise<void> {
        try {
            await api.put('/promotions/batch/status', { ids, status });
        } catch (error) {
            console.error('Error updating promotions status:', error);
            throw error;
        }
    },

    /**
     * Delete multiple promotions
     * @param ids Array of promotion IDs to delete
     */
    async deletePromotions(ids: string[]): Promise<void> {
        try {
            await api.delete('/promotions/batch', { data: { ids } });
        } catch (error) {
            console.error('Error deleting promotions:', error);
            throw error;
        }
    }
};

export default promotionApiService; 